import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import MaraSportsLogo from '../components/MaraSportsLogo';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { tournamentsApi, athletesApi, cartApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import TeamRegistrationDialog from '../components/TeamRegistrationDialog';
import {
    Trophy, Calendar, MapPin, Users, ArrowLeft,
    Clock, Award, Loader2, ShoppingCart, Check, UsersRound
} from 'lucide-react';

const TournamentDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const [tournament, setTournament] = useState(null);
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [registering, setRegistering] = useState(false);
    
    // Registration dialog state
    const [showRegistrationDialog, setShowRegistrationDialog] = useState(false);
    const [showTeamRegistrationDialog, setShowTeamRegistrationDialog] = useState(false);
    const [athleteProfile, setAthleteProfile] = useState(null);
    const [selectedAgeCategory, setSelectedAgeCategory] = useState('');
    const [selectedGenderCategory, setSelectedGenderCategory] = useState('');
    const [selectedDisciplines, setSelectedDisciplines] = useState([]);
    const [calculatedFee, setCalculatedFee] = useState(0);
    const [cartCount, setCartCount] = useState(0);
    const [eligibleAgeCategories, setEligibleAgeCategories] = useState([]);
    const [isTeamSport, setIsTeamSport] = useState(false);
    const [teamSportInfo, setTeamSportInfo] = useState(null);

    useEffect(() => {
        loadTournament();
        loadCartCount();
        checkIfTeamSport();
    }, [id]);

    const loadTournament = async () => {
        try {
            const [tournamentRes, matchesRes] = await Promise.all([
                tournamentsApi.getById(id),
                tournamentsApi.getMatches(id)
            ]);
            setTournament(tournamentRes.data);
            setMatches(matchesRes.data);
        } catch (error) {
            console.error('Failed to load tournament:', error);
            toast.error('Tournament not found');
            navigate('/tournaments');
        } finally {
            setLoading(false);
        }
    };

    const loadCartCount = async () => {
        if (!isAuthenticated) return;
        try {
            const res = await cartApi.getCart();
            setCartCount(res.data.item_count || 0);
        } catch (e) {}
    };

    const checkIfTeamSport = async () => {
        try {
            const res = await tournamentsApi.isTeamSport(id);
            setIsTeamSport(res.data.is_team_sport);
            setTeamSportInfo(res.data);
        } catch (e) {
            console.error('Failed to check team sport:', e);
        }
    };

    const loadAthleteProfile = async () => {
        try {
            const res = await athletesApi.getMyProfile();
            setAthleteProfile(res.data);
            return res.data;
        } catch (e) {
            return null;
        }
    };

    // Calculate athlete's age from DOB
    const calculateAge = (dob) => {
        if (!dob) return null;
        const today = new Date();
        const birthDate = new Date(dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    // Calculate registration fee based on disciplines
    const calculateRegistrationFee = (numDisciplines) => {
        if (!tournament) return 0;
        
        if (tournament.fee_per_discipline) {
            if (numDisciplines >= 5) return tournament.fee_5_disciplines || 0;
            if (numDisciplines === 4) return tournament.fee_4_disciplines || 0;
            if (numDisciplines === 3) return tournament.fee_3_disciplines || 0;
            if (numDisciplines === 2) return tournament.fee_2_disciplines || 0;
            return tournament.fee_1_discipline || 0;
        }
        return tournament.entry_fee || 0;
    };

    // Handle discipline selection
    const handleDisciplineToggle = (discipline) => {
        let newDisciplines;
        if (selectedDisciplines.includes(discipline)) {
            newDisciplines = selectedDisciplines.filter(d => d !== discipline);
        } else {
            newDisciplines = [...selectedDisciplines, discipline];
        }
        setSelectedDisciplines(newDisciplines);
        setCalculatedFee(calculateRegistrationFee(newDisciplines.length));
    };

    // Open registration dialog
    const handleOpenRegistration = async () => {
        if (!isAuthenticated) {
            toast.error('Please sign in to register');
            navigate('/auth');
            return;
        }

        // Use only the tournament's configured age categories - no default fallback
        const tournamentAgeGroups = tournament?.age_groups || [];
        
        // If tournament has no age groups configured, show error
        if (tournamentAgeGroups.length === 0) {
            toast.error('This tournament has no age categories configured. Please contact the organizer.');
            return;
        }

        // For team sports, open team registration dialog
        if (isTeamSport) {
            // Check user role - coaches, school admins, organizers can register in ALL age groups
            const nonAthleteRoles = ['coach', 'school_admin', 'organizer', 'admin', 'super_admin'];
            const isNonAthlete = nonAthleteRoles.includes(user?.role);
            
            if (isNonAthlete) {
                // Coaches/School Heads can register teams in ALL age categories
                setEligibleAgeCategories(tournamentAgeGroups);
                setShowTeamRegistrationDialog(true);
                return;
            }
            
            // For athletes registering a team, apply age restriction
            const profile = await loadAthleteProfile();
            if (!profile) {
                toast.error('Please complete your athlete profile first');
                navigate('/profile/me');
                return;
            }
            
            // Filter categories based on athlete's age
            let filteredCategories = tournamentAgeGroups;
            if (profile.date_of_birth) {
                const age = calculateAge(profile.date_of_birth);
                if (age !== null) {
                    filteredCategories = tournamentAgeGroups.filter(category => {
                        if (category === 'Senior' || category === 'Open') return true;
                        const match = category.match(/U(\d+)/);
                        if (!match) return true;
                        const categoryLimit = parseInt(match[1]);
                        // Category must be strictly greater than athlete's age
                        return categoryLimit > age;
                    });
                    
                    if (filteredCategories.length === 0) {
                        toast.error(`No eligible age categories for your age (${age} years). You need categories above U${age}. Available: ${tournamentAgeGroups.join(', ')}`);
                        return;
                    }
                }
            }
            
            setEligibleAgeCategories(filteredCategories);
            setShowTeamRegistrationDialog(true);
            return;
        }

        // For individual sports, load athlete profile to get DOB
        const profile = await loadAthleteProfile();
        if (!profile) {
            toast.error('Please complete your athlete profile first');
            navigate('/profile/me');
            return;
        }
        
        // Filter categories based on athlete's age
        // Rule: Athlete can only register for categories where the category limit is HIGHER than their age
        // Example: 15-year-old can play U17, U18, U19... but NOT U15 or U16
        let filteredCategories = tournamentAgeGroups;
        if (profile.date_of_birth) {
            const age = calculateAge(profile.date_of_birth);
            if (age !== null) {
                filteredCategories = tournamentAgeGroups.filter(category => {
                    if (category === 'Senior' || category === 'Open') return true;
                    const match = category.match(/U(\d+)/);
                    if (!match) return true;
                    const categoryLimit = parseInt(match[1]);
                    // Category must be strictly greater than athlete's age
                    // 15-year-old can play U16 (16 > 15), U17, U18, etc.
                    return categoryLimit > age;
                });
                
                // If no eligible categories for this age
                if (filteredCategories.length === 0) {
                    toast.error(`No eligible age categories for your age (${age} years). You need categories above U${age}. Available: ${tournamentAgeGroups.join(', ')}`);
                    return;
                }
            }
        }
        setEligibleAgeCategories(filteredCategories);

        // Reset selection
        setSelectedAgeCategory('');
        setSelectedGenderCategory('');
        setSelectedDisciplines([]);
        setCalculatedFee(0);
        setShowRegistrationDialog(true);
    };

    // Handle team registration submission
    const handleTeamRegistrationSubmit = async (registrationData) => {
        setRegistering(true);
        try {
            await tournamentsApi.registerTeam(id, registrationData);
            toast.success('Team added to cart! Proceed to checkout to complete registration.');
            setShowTeamRegistrationDialog(false);
            loadTournament();
            loadCartCount();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to register team');
        } finally {
            setRegistering(false);
        }
    };

    // Submit registration
    const handleSubmitRegistration = async () => {
        if (!selectedAgeCategory) {
            toast.error('Please select an age category');
            return;
        }
        if (selectedDisciplines.length === 0) {
            toast.error('Please select at least one discipline/event');
            return;
        }

        setRegistering(true);
        try {
            await tournamentsApi.registerDetailed(id, {
                age_category: selectedAgeCategory,
                gender_category: selectedGenderCategory,
                disciplines: selectedDisciplines
            });
            toast.success('Added to cart! Proceed to checkout to complete registration.');
            setShowRegistrationDialog(false);
            loadTournament();
            loadCartCount();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to register');
        } finally {
            setRegistering(false);
        }
    };

    // Get currency symbol
    const getCurrencySymbol = (currency) => {
        const symbols = { KES: 'KSh ', USD: '$', EUR: '€', GBP: '£' };
        if (!currency) return 'KSh '; // Default to Kenya Shillings
        return symbols[currency] || currency + ' ';
    };

    const getStatusColor = (tournament_status) => {
        switch (tournament_status) {
            case 'registration_open': return 'text-success bg-success/10 border-success/20';
            case 'in_progress': return 'text-secondary bg-secondary/10 border-secondary/20';
            case 'upcoming': return 'text-accent bg-accent/10 border-accent/20';
            case 'completed': return 'text-muted-foreground bg-muted border-white/10';
            default: return 'text-muted-foreground bg-muted border-white/10';
        }
    };

    const getStatusLabel = (tournament_status) => {
        switch (tournament_status) {
            case 'registration_open': return 'Registration Open';
            case 'in_progress': return 'In Progress';
            case 'upcoming': return 'Upcoming';
            case 'completed': return 'Completed';
            default: return tournament_status;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!tournament) {
        return null;
    }

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Grid overlay */}
            <div className="fixed inset-0 grid-lines pointer-events-none z-0" />

            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Link to="/tournaments" className="flex items-center gap-2 text-muted-foreground hover:text-white">
                                <ArrowLeft className="w-4 h-4" />
                                <span className="text-sm hidden sm:inline">Back to Tournaments</span>
                            </Link>
                        </div>
                        <Link to="/" className="flex items-center gap-2">
                            <MaraSportsLogo className="h-8" />
                            <span className="font-heading font-bold text-lg tracking-tight uppercase hidden sm:block">
                                NextGen<span className="text-primary"> Tournaments</span>
                            </span>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative py-12 md:py-16 border-b border-white/5">
                <div className="hero-glow absolute inset-0 z-0" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                        <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-3 mb-4">
                                <span className={`text-xs px-3 py-1 border ${getStatusColor(tournament.tournament_status)}`}>
                                    {getStatusLabel(tournament.tournament_status)}
                                </span>
                                <span className="text-xs px-3 py-1 bg-white/5 text-muted-foreground">
                                    {tournament.sport}
                                </span>
                            </div>
                            <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter uppercase mb-4" data-testid="tournament-name">
                                {tournament.name}
                            </h1>
                            <p className="text-muted-foreground text-lg max-w-2xl">
                                {tournament.description || 'Join this exciting competition and showcase your skills against the best athletes in Africa.'}
                            </p>
                        </div>
                        
                        <div className="flex items-center gap-4">
                            {isAuthenticated && cartCount > 0 && (
                                <Link 
                                    to="/cart" 
                                    className="relative flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                                    data-testid="cart-link"
                                >
                                    <ShoppingCart className="w-5 h-5" />
                                    <span className="text-sm">Cart</span>
                                    <span className="absolute -top-2 -right-2 bg-primary text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                                        {cartCount}
                                    </span>
                                </Link>
                            )}
                            {tournament.tournament_status === 'registration_open' && (
                                <Button
                                    onClick={handleOpenRegistration}
                                    disabled={registering}
                                    className="bg-primary hover:bg-red-600 text-white btn-glow uppercase tracking-widest text-sm font-bold h-12 px-8"
                                    data-testid="register-btn"
                                >
                                    {registering ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : isTeamSport ? (
                                        <>
                                            <UsersRound className="w-4 h-4 mr-2" />
                                            Register Team
                                        </>
                                    ) : (
                                        'Register Now'
                                    )}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Details Grid */}
            <section className="py-12">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Main Info */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Event Details */}
                            <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                                <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-6">
                                    Event Details
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <MapPin className="w-5 h-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Location</p>
                                            <p className="font-medium">{tournament.location}</p>
                                            {tournament.venue && <p className="text-sm text-muted-foreground">{tournament.venue}</p>}
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-accent/10 flex items-center justify-center flex-shrink-0">
                                            <Calendar className="w-5 h-5 text-accent" />
                                        </div>
                                        <div>
                                            <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Dates</p>
                                            <p className="font-medium">{tournament.start_date}</p>
                                            <p className="text-sm text-muted-foreground">to {tournament.end_date}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                            <Users className="w-5 h-5 text-secondary" />
                                        </div>
                                        <div>
                                            <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Participants</p>
                                            <p className="font-medium">{tournament.current_participants} / {tournament.max_participants}</p>
                                            <div className="w-32 h-2 bg-white/5 mt-2">
                                                <div 
                                                    className="h-full bg-secondary" 
                                                    style={{ width: `${(tournament.current_participants / tournament.max_participants) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {tournament.registration_deadline && (
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 bg-warning/10 flex items-center justify-center flex-shrink-0">
                                                <Clock className="w-5 h-5 text-warning" />
                                            </div>
                                            <div>
                                                <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Registration Deadline</p>
                                                <p className="font-medium">{tournament.registration_deadline}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Age Categories */}
                            {tournament.age_groups?.length > 0 && (
                                <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                                    <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-6">
                                        Age Categories
                                    </h2>
                                    <div className="flex flex-wrap gap-3">
                                        {tournament.age_groups.map((category) => (
                                            <span key={category} className="px-4 py-2 bg-white/5 border border-white/10 text-sm">
                                                {category}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Gender Categories */}
                            {tournament.gender_categories?.length > 0 && (
                                <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                                    <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-6">
                                        Gender Categories
                                    </h2>
                                    <div className="flex flex-wrap gap-3">
                                        {tournament.gender_categories.map((gender) => (
                                            <span key={gender} className="px-4 py-2 bg-white/5 border border-white/10 text-sm">
                                                {gender}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Disciplines/Events */}
                            {tournament.disciplines?.length > 0 && (
                                <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                                    <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-6">
                                        Events / Disciplines
                                    </h2>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {tournament.disciplines.map((discipline) => (
                                            <span key={discipline} className="px-4 py-2 bg-primary/10 border border-primary/20 text-sm text-center">
                                                {discipline}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Schedule/Matches */}
                            {(tournament.schedule?.length > 0 || matches.length > 0) && (
                                <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                                    <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-6">
                                        Schedule
                                    </h2>
                                    <div className="space-y-3">
                                        {(tournament.schedule || matches).slice(0, 10).map((item, index) => (
                                            <div key={index} className="flex items-center justify-between p-4 bg-[#0a1628] border border-white/5">
                                                <div>
                                                    <p className="text-sm font-medium">{item.round || item.round_name}</p>
                                                    <p className="text-xs text-muted-foreground">Match {item.match_number}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm">{item.time || item.scheduled_time || 'TBD'}</p>
                                                    <p className="text-xs text-muted-foreground">{item.venue}</p>
                                                </div>
                                            </div>
                                        ))}
                                        {(tournament.schedule?.length === 0 && matches.length === 0) && (
                                            <p className="text-center text-muted-foreground py-8">Schedule will be announced soon</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Prize Info */}
                            <div className="bg-[#0d2847] border border-white/5 p-6">
                                <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-6">
                                    Prize Information
                                </h3>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground">Entry Fee</span>
                                        <span className="font-mono text-lg">
                                            {tournament.currency === 'KES' ? 'KSh ' : tournament.currency === 'USD' ? '$' : tournament.currency === 'EUR' ? '€' : tournament.currency === 'GBP' ? '£' : (tournament.currency || '$') + ' '}
                                            {tournament.entry_fee?.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="border-t border-white/5 pt-4 flex items-center justify-between">
                                        <span className="text-muted-foreground">Prize Pool</span>
                                        <span className="font-mono text-2xl text-accent">
                                            {tournament.currency === 'KES' ? 'KSh ' : tournament.currency === 'USD' ? '$' : tournament.currency === 'EUR' ? '€' : tournament.currency === 'GBP' ? '£' : (tournament.currency || '$') + ' '}
                                            {tournament.prize_pool?.toLocaleString()}
                                        </span>
                                    </div>
                                    {tournament.prize_1st > 0 && (
                                        <div className="border-t border-white/5 pt-4 space-y-2">
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Prize Breakdown</p>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-muted-foreground">1st Place</span>
                                                <span className="text-accent">
                                                    {tournament.currency === 'KES' ? 'KSh ' : tournament.currency === 'USD' ? '$' : (tournament.currency || '$') + ' '}
                                                    {tournament.prize_1st?.toLocaleString()}
                                                </span>
                                            </div>
                                            {tournament.prize_2nd > 0 && (
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-muted-foreground">2nd Place</span>
                                                    <span>
                                                        {tournament.currency === 'KES' ? 'KSh ' : tournament.currency === 'USD' ? '$' : (tournament.currency || '$') + ' '}
                                                        {tournament.prize_2nd?.toLocaleString()}
                                                    </span>
                                                </div>
                                            )}
                                            {tournament.prize_3rd > 0 && (
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-muted-foreground">3rd Place</span>
                                                    <span>
                                                        {tournament.currency === 'KES' ? 'KSh ' : tournament.currency === 'USD' ? '$' : (tournament.currency || '$') + ' '}
                                                        {tournament.prize_3rd?.toLocaleString()}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Organizer */}
                            <div className="bg-[#0d2847] border border-white/5 p-6">
                                <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-4">
                                    Organizer
                                </h3>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary/20 flex items-center justify-center">
                                        <Award className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{tournament.organizer_name || 'NextGen Tournaments'}</p>
                                        <p className="text-xs text-muted-foreground">Official Organizer</p>
                                    </div>
                                </div>
                            </div>

                            {/* Quick Stats */}
                            <div className="bg-[#0d2847] border border-white/5 p-6">
                                <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-4">
                                    Quick Stats
                                </h3>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">Sport</span>
                                        <span>{tournament.sport}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">Duration</span>
                                        <span>{Math.ceil((new Date(tournament.end_date) - new Date(tournament.start_date)) / (1000 * 60 * 60 * 24) + 1)} days</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">Categories</span>
                                        <span>{tournament.age_categories?.length || 0}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Registration Dialog - Individual Sports */}
            <Dialog open={showRegistrationDialog} onOpenChange={setShowRegistrationDialog}>
                <DialogContent className="bg-[#0d2847] border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl uppercase tracking-tight">
                            Register for {tournament.name}
                        </DialogTitle>
                        <DialogDescription className="text-muted-foreground">
                            Select your category and events to register
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        {/* Athlete Info */}
                        {athleteProfile && (
                            <div className="bg-[#0a1628] p-4 border border-white/5 rounded">
                                <p className="text-sm text-muted-foreground mb-1">Registering as</p>
                                <p className="font-medium">{athleteProfile.full_name}</p>
                                {athleteProfile.date_of_birth && (
                                    <p className="text-sm text-muted-foreground">
                                        Age: {calculateAge(athleteProfile.date_of_birth)} years
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Age Category Selection */}
                        <div className="space-y-2">
                            <Label className="text-sm text-muted-foreground uppercase tracking-wider">
                                Age Category *
                            </Label>
                            <select
                                value={selectedAgeCategory}
                                onChange={(e) => setSelectedAgeCategory(e.target.value)}
                                className="w-full h-10 px-3 bg-[#0a1628] border border-white/10 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                data-testid="age-category-select"
                            >
                                <option value="" disabled>Select your age category</option>
                                {eligibleAgeCategories.map((category) => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                            {athleteProfile?.date_of_birth && (
                                <p className="text-xs text-muted-foreground">
                                    Showing eligible categories for age {calculateAge(athleteProfile.date_of_birth)}
                                </p>
                            )}
                        </div>

                        {/* Gender Category Selection (if available) */}
                        {tournament.gender_categories?.length > 0 && (
                            <div className="space-y-2">
                                <Label className="text-sm text-muted-foreground uppercase tracking-wider">
                                    Gender Category
                                </Label>
                                <select
                                    value={selectedGenderCategory}
                                    onChange={(e) => setSelectedGenderCategory(e.target.value)}
                                    className="w-full h-10 px-3 bg-[#0a1628] border border-white/10 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                    data-testid="gender-category-select"
                                >
                                    <option value="" disabled>Select gender category</option>
                                    {tournament.gender_categories.map((gender) => (
                                        <option key={gender} value={gender}>
                                            {gender}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Disciplines/Events Selection */}
                        {tournament.disciplines?.length > 0 && (
                            <div className="space-y-3">
                                <Label className="text-sm text-muted-foreground uppercase tracking-wider">
                                    Select Events/Disciplines *
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                    Click to select the events you want to participate in
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {tournament.disciplines.map((discipline) => (
                                        <button
                                            key={discipline}
                                            type="button"
                                            onClick={() => handleDisciplineToggle(discipline)}
                                            className={`p-3 text-sm text-left border transition-all ${
                                                selectedDisciplines.includes(discipline)
                                                    ? 'bg-primary/20 border-primary text-white'
                                                    : 'bg-[#0a1628] border-white/10 text-muted-foreground hover:border-white/30'
                                            }`}
                                            data-testid={`discipline-${discipline.replace(/\s+/g, '-').toLowerCase()}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                {selectedDisciplines.includes(discipline) ? (
                                                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                                                ) : (
                                                    <div className="w-4 h-4 border border-white/20 flex-shrink-0" />
                                                )}
                                                <span className="truncate">{discipline}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {selectedDisciplines.length} event{selectedDisciplines.length !== 1 ? 's' : ''} selected
                                </p>
                            </div>
                        )}

                        {/* No Disciplines - Simple Registration */}
                        {(!tournament.disciplines || tournament.disciplines.length === 0) && (
                            <div className="bg-accent/10 border border-accent/20 p-4 rounded">
                                <p className="text-sm text-accent">
                                    This tournament uses a flat registration fee. Select your age category and proceed.
                                </p>
                            </div>
                        )}

                        {/* Fee Summary */}
                        <div className="bg-[#0a1628] p-4 border border-white/5 rounded space-y-3">
                            <h4 className="font-heading font-bold uppercase tracking-tight text-sm">
                                Registration Summary
                            </h4>
                            
                            {/* Show pricing tiers for per-discipline tournaments */}
                            {tournament.fee_per_discipline && (
                                <div className="space-y-2 text-xs border-b border-white/10 pb-3 mb-3">
                                    <p className="text-muted-foreground uppercase tracking-wider">Fee Structure:</p>
                                    <div className="grid grid-cols-2 gap-1 text-muted-foreground">
                                        {tournament.fee_1_discipline && (
                                            <div className={`flex justify-between px-2 py-1 rounded ${selectedDisciplines.length === 1 ? 'bg-primary/20 text-white' : ''}`}>
                                                <span>1 event:</span>
                                                <span>{getCurrencySymbol(tournament.currency)}{tournament.fee_1_discipline}</span>
                                            </div>
                                        )}
                                        {tournament.fee_2_disciplines && (
                                            <div className={`flex justify-between px-2 py-1 rounded ${selectedDisciplines.length === 2 ? 'bg-primary/20 text-white' : ''}`}>
                                                <span>2 events:</span>
                                                <span>{getCurrencySymbol(tournament.currency)}{tournament.fee_2_disciplines}</span>
                                            </div>
                                        )}
                                        {tournament.fee_3_disciplines && (
                                            <div className={`flex justify-between px-2 py-1 rounded ${selectedDisciplines.length === 3 ? 'bg-primary/20 text-white' : ''}`}>
                                                <span>3 events:</span>
                                                <span>{getCurrencySymbol(tournament.currency)}{tournament.fee_3_disciplines}</span>
                                            </div>
                                        )}
                                        {tournament.fee_4_disciplines && (
                                            <div className={`flex justify-between px-2 py-1 rounded ${selectedDisciplines.length === 4 ? 'bg-primary/20 text-white' : ''}`}>
                                                <span>4 events:</span>
                                                <span>{getCurrencySymbol(tournament.currency)}{tournament.fee_4_disciplines}</span>
                                            </div>
                                        )}
                                        {tournament.fee_5_disciplines && (
                                            <div className={`flex justify-between px-2 py-1 rounded ${selectedDisciplines.length >= 5 ? 'bg-primary/20 text-white' : ''}`}>
                                                <span>5+ events:</span>
                                                <span>{getCurrencySymbol(tournament.currency)}{tournament.fee_5_disciplines}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {tournament.fee_per_discipline && selectedDisciplines.length > 0 && (
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between text-muted-foreground">
                                        <span>Events Selected</span>
                                        <span>{selectedDisciplines.length} ({selectedDisciplines.join(', ')})</span>
                                    </div>
                                </div>
                            )}
                            
                            <div className="border-t border-white/10 pt-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-muted-foreground">Registration Fee</span>
                                    <span className="font-mono text-xl text-accent" data-testid="registration-fee">
                                        {getCurrencySymbol(tournament.currency)}
                                        {calculatedFee > 0 ? calculatedFee.toLocaleString() : (tournament.registration_fee || tournament.entry_fee || 0).toLocaleString()}
                                    </span>
                                </div>
                            </div>
                            
                            {tournament.fee_per_discipline && (
                                <p className="text-xs text-muted-foreground">
                                    Fee varies based on number of events selected
                                </p>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => setShowRegistrationDialog(false)}
                                className="flex-1 border-white/10 hover:bg-white/5"
                                data-testid="cancel-registration-btn"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmitRegistration}
                                disabled={registering || !selectedAgeCategory || (tournament.disciplines?.length > 0 && selectedDisciplines.length === 0)}
                                className="flex-1 bg-primary hover:bg-red-600 text-white"
                                data-testid="confirm-registration-btn"
                            >
                                {registering ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                    <ShoppingCart className="w-4 h-4 mr-2" />
                                )}
                                Add to Cart
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Team Registration Dialog */}
            <TeamRegistrationDialog
                open={showTeamRegistrationDialog}
                onOpenChange={setShowTeamRegistrationDialog}
                tournament={tournament}
                eligibleAgeCategories={eligibleAgeCategories}
                onSubmit={handleTeamRegistrationSubmit}
                isSubmitting={registering}
            />
        </div>
    );
};

export default TournamentDetailPage;
